import createDataContext from './createDataContext';
import {
  IMDB_URL,
  MOVIE_URL,
  MOVIE_GENRE_LIST_URL,
  TRENDING_MOVIES_URL,
  TRENDING_TV_SHOWS_URL,
  TV_SHOW_GENRE_LIST_URL,
  API_KEY,
  TV_SHOW_URL,
} from '../config';

const courseReducer = (state, action) => {
  switch (action.type) {
    case 'fetch_movies':
      return { ...state, movies: action.payload };
    case 'fetch_tv_shows':
      return { ...state, tvShows: action.payload };
    case 'fetch_movie_genres':
      return { ...state, movieGenres: action.payload };
    case 'fetch_tv_show_genres':
      return { ...state, tvShowGenres: action.payload };
    case 'add_error':
      return { ...state, errors: state.errors.push(action.payload) };
    case 'add_movie_details':
      return {
        ...state,
        movies: state.movies.map((movie) => {
          return movie.id === action.payload.id ? action.payload : movie;
        }),
      };
    case 'add_tv_show_details':
      return {
        ...state,
        tvShows: state.tvShows.map((tvShow) => {
          return tvShow.id === action.payload.id ? action.payload : tvShow;
        }),
      };
    case 'fetch_tv_show_link':
      return {
        ...state,
        tvShows: state.tvShows.map((tvShow) => {
          return tvShow.id === action.payload.id
            ? {
                ...tvShow,
                imdb_id: action.payload.imdb_id,
              }
            : tvShow;
        }),
      };
    default:
      return state;
  }
};

// Action to fetch list of trending movies.
const fetchMovies = (dispatch) => {
  return async () => {
    try {
      const response = await fetch(TRENDING_MOVIES_URL);
      const jsonResponse = await response.json();
      dispatch({
        type: 'fetch_movies',
        payload: jsonResponse.results,
      });
    } catch (error) {
      dispatch({
        type: 'add_error',
        payload: {
          type: 'movie_list',
          message: "Couldn't fetch list of movies",
        },
      });
    }
  };
};

// Action to fetch list of trending tv shows.
const fetchTvShows = (dispatch) => {
  return async () => {
    try {
      const response = await fetch(TRENDING_TV_SHOWS_URL);
      const jsonResponse = await response.json();
      dispatch({
        type: 'fetch_tv_shows',
        payload: jsonResponse.results,
      });
    } catch (error) {
      dispatch({
        type: 'add_error',
        payload: {
          type: 'tv_show_list',
          message: "Couldn't fetch list of tv shows",
        },
      });
    }
  };
};

// Action to fetch list of genres for movies.
const fetchMovieGenres = (dispatch) => {
  return async () => {
    try {
      const response = await fetch(MOVIE_GENRE_LIST_URL);
      const genreResponse = await response.json();
      const genreIdMap = {};
      for (let i = 0; i < genreResponse.genres.length; i += 1) {
        genreIdMap[`${genreResponse.genres[i].id}`] =
          genreResponse.genres[i].name;
      }
      dispatch({
        type: 'fetch_movie_genres',
        payload: genreIdMap,
      });
    } catch (error) {
      dispatch({
        type: 'add_error',
        payload: {
          type: 'movie_genre_list',
          message: "Couldn't fetch list of genres for movies",
        },
      });
    }
  };
};

// Action to fetch list of genres for tv shows.
const fetchTvShowGenres = (dispatch) => {
  return async () => {
    try {
      const response = await fetch(TV_SHOW_GENRE_LIST_URL);
      const genreResponse = await response.json();
      const genreIdMap = {};
      for (let i = 0; i < genreResponse.genres.length; i += 1) {
        genreIdMap[`${genreResponse.genres[i].id}`] =
          genreResponse.genres[i].name;
      }
      dispatch({
        type: 'fetch_tv_show_genres',
        payload: genreIdMap,
      });
    } catch (error) {
      dispatch({
        type: 'add_error',
        payload: {
          type: 'tv_show_genre_list',
          message: "Couldn't fetch list of genres for tv shows",
        },
      });
    }
  };
};

// Action to fetch movie details.
const fetchMovieDetails = (dispatch) => {
  return async (id) => {
    try {
      const response = await fetch(`${MOVIE_URL}${id}?api_key=${API_KEY}`);
      const movieDetailResponse = await response.json();
      dispatch({
        type: 'add_movie_details',
        payload: movieDetailResponse,
      });
      window.open(`${IMDB_URL}${movieDetailResponse.imdb_id}/`, '_blank');
    } catch (error) {
      dispatch({
        type: 'add_error',
        payload: {
          type: 'movie_detail',
          message: "Couldn't fetch movie details",
        },
      });
    }
  };
};

// Action to fetch tv show details.
const fetchTvShowDetails = (dispatch) => {
  return async (id) => {
    try {
      const response = await fetch(`${TV_SHOW_URL}${id}?api_key=${API_KEY}`);
      const tvShowDetailResponse = await response.json();
      dispatch({
        type: 'add_tv_show_details',
        payload: tvShowDetailResponse,
      });
    } catch (error) {
      dispatch({
        type: 'add_error',
        payload: {
          type: 'tv_show_detail',
          message: "Couldn't fetch tv show details",
        },
      });
    }
  };
};

// Action to fetch tv show link.
const fetchTvShowLink = (dispatch) => {
  return async (id) => {
    try {
      const response = await fetch(
        `${TV_SHOW_URL}${id}/external_ids?api_key=${API_KEY}`
      );
      const tvShowLinkResponse = await response.json();
      dispatch({
        type: 'fetch_tv_show_link',
        payload: tvShowLinkResponse,
      });
      window.open(`${IMDB_URL}${tvShowLinkResponse.imdb_id}/`, '_blank');
    } catch (error) {
      dispatch({
        type: 'add_error',
        payload: {
          type: 'tv_show_link',
          message: "Couldn't fetch tv show link",
        },
      });
    }
  };
};

// Using helper function to create Context and Provider.
export const { Context, Provider } = createDataContext(
  courseReducer,
  {
    fetchMovies,
    fetchTvShows,
    fetchMovieGenres,
    fetchTvShowGenres,
    fetchMovieDetails,
    fetchTvShowDetails,
    fetchTvShowLink,
  },
  {
    movies: [],
    tvShows: [],
    movieGenres: {},
    tvShowGenres: {},
    errors: [],
  }
);
